import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { useAuth } from './AuthContext';

const AppContext = createContext(null);

export function AppProvider({ children }) {
  const { user } = useAuth();
  const [projects, setProjects] = useState([]);
  const [activeProject, setActiveProject] = useState('all');
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [theme, setTheme] = useState('light');

  // Load workspaces
  const fetchProjects = useCallback(async () => {
    if (!user) return;
    try {
      const { data } = await api.get('/projects');
      setProjects(data.projects);
    } catch {}
  }, [user]);

  // Load notifications
  const fetchNotifications = useCallback(async () => {
    if (!user) return;
    try {
      const { data } = await api.get('/notifications');
      setNotifications(data.notifications);
      setUnreadCount(data.unread_count);
    } catch {}
  }, [user]);

  useEffect(() => {
    if (user) {
      fetchProjects();
      fetchNotifications();
      setTheme(user.theme || 'light');
      setActiveProject(user.last_project_filter || 'all');
    }
  }, [user, fetchProjects, fetchNotifications]);

  // Apply theme
  useEffect(() => {
    document.documentElement.classList.toggle('dark', theme === 'dark');
  }, [theme]);

  const toggleTheme = async () => {
    const next = theme === 'light' ? 'dark' : 'light';
    setTheme(next);
    try { await api.put('/user/preferences', { theme: next }); } catch {}
  };

  const switchProject = async (projectId) => {
    setActiveProject(projectId);
    try { await api.put('/user/preferences', { last_project_filter: projectId }); } catch {}
  };

  return (
    <AppContext.Provider value={{
      projects, setProjects, fetchProjects,
      activeProject, switchProject,
      notifications, unreadCount, fetchNotifications,
      theme, toggleTheme,
    }}>
      {children}
    </AppContext.Provider>
  );
}

export const useApp = () => useContext(AppContext);
